// @ts-check
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { automationsApi } from "@/api/endpoints/automations.js";
import { toast } from "@/shared/ui/toaster.jsx";

/**
 * Hooks das jornadas de automação.
 *
 * Antes, tudo isso era `useState` + `localStorage.setItem` dentro do
 * componente da tela: as jornadas viviam só no navegador de quem as criou.
 */

const CHAVE = ["automations"];

export function useAutomations() {
  return useQuery({
    queryKey: CHAVE,
    queryFn: async () => (await automationsApi.list()).automations,
  });
}

/** Fábrica das mutações — todas invalidam a mesma consulta. */
function useAutomationMutation(mutationFn, mensagemSucesso) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: CHAVE });
      if (mensagemSucesso) toast.success(mensagemSucesso);
    },
    onError: (erro) => toast.error(erro.message),
  });
}

export function useCreateAutomation() {
  return useAutomationMutation(
    /** @param {object} dados */
    async (dados) => (await automationsApi.create(dados)).automation,
    "Jornada criada.",
  );
}

export function useUpdateAutomation() {
  return useAutomationMutation(
    /** @param {{ id: string } & Record<string, unknown>} variaveis */
    async ({ id, ...patch }) => (await automationsApi.update(id, patch)).automation,
  );
}

export function useDeleteAutomation() {
  return useAutomationMutation(
    /** @param {string} id */
    (id) => automationsApi.remove(id),
    "Jornada excluída.",
  );
}

export function useSaveStep() {
  return useAutomationMutation(
    /** @param {{ automationId: string, stepId?: string|null } & Record<string, unknown>} variaveis */
    ({ automationId, stepId, ...dados }) =>
      stepId
        ? automationsApi.updateStep(automationId, stepId, dados)
        : automationsApi.addStep(automationId, dados),
    "Etapa salva.",
  );
}

export function useDeleteStep() {
  return useAutomationMutation(
    /** @param {{ automationId: string, stepId: string }} variaveis */
    ({ automationId, stepId }) => automationsApi.removeStep(automationId, stepId),
    "Etapa removida.",
  );
}

export function useReorderSteps() {
  return useAutomationMutation(
    /** @param {{ automationId: string, stepIds: string[] }} variaveis */
    ({ automationId, stepIds }) => automationsApi.reorderSteps(automationId, stepIds),
  );
}
